/**
 * ============================================================================
 *  useTheme — 深色/浅色主题切换（localStorage + 自定义事件广播 + data-theme）
 * ============================================================================
 *
 * 左下角主题按钮切换整个界面配色。深色为默认（保持现状），浅色走墨黑高反差配色。
 * 配色令牌定义在 index.css：:root 为深色默认值，[data-theme="light"] 覆盖为浅色。
 * 本 Hook 只负责把选择写进 <html data-theme> 并持久化，颜色由 CSS 变量接管。
 *
 * 与 useSnapToggle 同款模式：localStorage + 广播事件解耦，同时持久化记住用户选择。
 *
 * 默认深色（首次无 localStorage 记录时返回 'dark'）。
 *
 * 数据流：
 *   localStorage('ui_theme') ← setTheme()
 *   ↓ 广播 THEME_CHANGED + 写 <html data-theme>
 *   各处监听 → 同步状态
 */
import { useState, useEffect } from 'react';

const STORAGE_KEY = 'ui_theme';
export const THEME_CHANGED = 'ui_theme_changed';

export function getTheme() {
  // 默认深色：只有显式存了 'light' 才走浅色
  return localStorage.getItem(STORAGE_KEY) === 'light' ? 'light' : 'dark';
}

// 把主题写到 <html data-theme>，让 CSS 变量令牌生效。深色不打属性（走 :root 默认值）。
export function applyTheme(theme) {
  const root = document.documentElement;
  if (theme === 'light') root.setAttribute('data-theme', 'light');
  else root.removeAttribute('data-theme');
}

export function setTheme(theme) {
  const next = theme === 'light' ? 'light' : 'dark';
  localStorage.setItem(STORAGE_KEY, next);
  applyTheme(next);
  window.dispatchEvent(new Event(THEME_CHANGED));
}

// React Hook：返回 [theme, toggle]，自动跟随广播同步，并在挂载时应用一次
export function useTheme() {
  const [theme, setThemeState] = useState(getTheme);
  useEffect(() => {
    applyTheme(getTheme());
    const sync = () => setThemeState(getTheme());
    window.addEventListener(THEME_CHANGED, sync);
    return () => window.removeEventListener(THEME_CHANGED, sync);
  }, []);
  const toggle = () => setTheme(getTheme() === 'light' ? 'dark' : 'light');
  return [theme, toggle];
}
